// Assign-tag mutation: owns toast feedback (offline / manager-taken /
// generic / success). No cache invalidation — the table is SSE-fed and the
// next snapshot carries the change.

import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  assignTag,
  ManagerTagTakenError,
  TagOfflineError,
  unassignTag,
  type AssignTagInput,
} from '@apis/adminTags';
import { fetchHoistAvailability, fetchTagCertificatesCatalog } from '@apis/adminTags';
import type { IToast } from '@components/ui/Toasts/useToasts';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

/** Certificate-name catalog — the assign dialog's autocomplete cache. */
const adminTagCertificatesKey = (siteId: string) =>
  ['admin', 'tags', siteId, 'certificates'] as const;

/** Hoist-kind gate cache — see useHoistAvailability. */
const adminTagHoistAvailabilityKey = (siteId: string) =>
  ['admin', 'tags', siteId, 'hoist-availability'] as const;

type ToastPush = (toast: Omit<IToast, 'id'>) => void;

export function useAssignTag(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ tagId, input }: { tagId: string; input: AssignTagInput }) =>
      assignTag(siteId!, tagId, input),
    onSuccess: () => {
      push({ kind: 'success', title: t('adminTags.assign.success') });
    },
    onError: (err) => {
      push({
        kind: 'error',
        title: t(
          err instanceof TagOfflineError
            ? 'adminTags.assign.offlineError'
            : err instanceof ManagerTagTakenError
              ? 'adminTags.assign.managerTakenError'
              : 'adminTags.assign.error',
        ),
      });
    },
  });
}

export function useUnassignTag(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (tagId: string) => unassignTag(siteId!, tagId),
    onSuccess: () => {
      push({ kind: 'success', title: t('adminTags.unassignFlow.success') });
    },
    onError: () => {
      push({ kind: 'error', title: t('adminTags.unassignFlow.error') });
    },
  });
}

/** Certificate-name catalog for the dialog's autocomplete; null siteId = off. */
export function useTagCertificatesCatalog(siteId: string | null) {
  return useQuery({
    queryKey: adminTagCertificatesKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchTagCertificatesCatalog(siteId!, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Hoist-kind gate: does the project have a floor flagged as the hoist? */
export function useHoistAvailability(siteId: string | null) {
  return useQuery({
    queryKey: adminTagHoistAvailabilityKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchHoistAvailability(siteId!, signal),
    enabled: !!siteId,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}
